import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import VideoSection from '../components/VideoSection';
import TextCategoryGrid from '../components/TextCategoryGrid';
import ProductCategoriesGrid from '../components/ProductCategoriesGrid';
import {
  Smartphone,
  Palette,
  Heart,
  Star,
  ArrowRight,
  Truck,
  Headphones,
  ShoppingBag,
  Award,
  Wifi
} from 'lucide-react';

const Home = () => {
  const { addToCart } = useCart();
  const { settings } = useSiteSettings();

  // State for Supabase images (same as Gallery and SuperAdmin approach)
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for background slideshow
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [backgroundImages, setBackgroundImages] = useState([]);

  // Load images with admin metadata from all storage providers
  const loadSupabaseImages = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🖼️ Loading images from all storage providers for Home...');

      const { default: hybridImageService } = await import('../services/hybridImageService');

      // Load images from all sources (Supabase + Vercel Blob)
      const allImages = await hybridImageService.getAllImagesFromBothSources();

      if (allImages && allImages.length > 0) {
        // Only include images with admin-added metadata
        const imagesWithMetadata = allImages.filter(image =>
          image.hasMetadata && image.title && image.title.trim()
        ).map(image => ({
          ...image,
          source: `${image.storage}_hybrid`,
          // Ensure consistent URL field
          publicUrl: image.url || image.publicUrl
        }));

        console.log(`✅ Loaded ${imagesWithMetadata.length} images with metadata from all sources for Home`);
        console.log(`📊 Storage breakdown: ${allImages.filter(img => img.storage === 'vercel-blob').length} Vercel Blob, ${allImages.filter(img => img.storage === 'supabase').length} Supabase`);

        setImages(imagesWithMetadata);

        // Set background images for slideshow (featured images)
        const featuredImages = imagesWithMetadata.filter(img => img.featured);
        if (featuredImages.length > 0) {
          setBackgroundImages(featuredImages.map(img => img.url || img.publicUrl));
        } else {
          // Fallback to first few images if no featured images
          setBackgroundImages(imagesWithMetadata.slice(0, 5).map(img => img.url || img.publicUrl));
        }
      } else {
        setImages([]);
        console.log('ℹ️ No images found in Supabase storage');
        // Set fallback background images
        setBackgroundImages([
          '/images/products/arduino-uno.png',
          '/images/products/arduino-mega.png',
          '/images/products/esp32.png',
          '/images/products/breadboard.png',
          '/images/products/hc-sr04.png',
          '/images/products/arduino-nano.png'
        ]);
      }
    } catch (err) {
      console.error('❌ Error loading images for Home:', err);
      setError(err.message);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  // NO automatic data generation - all metadata comes from Firebase admin entries

  // Load images on component mount and set up refresh listener
  useEffect(() => {
    loadSupabaseImages();

    // Listen for global refresh events from SuperAdmin
    const handleGlobalRefresh = () => {
      console.log('🔄 Home page refreshing due to SuperAdmin changes...');
      loadSupabaseImages();
    };

    // Set up global refresh listener
    window.addEventListener('refreshAllImages', handleGlobalRefresh);

    // Also set up a global function that SuperAdmin can call
    if (!window.refreshHomeImages) {
      window.refreshHomeImages = loadSupabaseImages;
    }

    // Cleanup on unmount
    return () => {
      window.removeEventListener('refreshAllImages', handleGlobalRefresh);
      delete window.refreshHomeImages;
    };
  }, []);

  // Background slideshow effect
  useEffect(() => {
    if (backgroundImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentBgIndex((prevIndex) =>
        (prevIndex + 1) % backgroundImages.length
      );
    }, 4000); // Change background every 4 seconds

    return () => clearInterval(interval);
  }, [backgroundImages]);

  // Handle adding image to cart
  const handleAddToCart = (image) => {
    if (image.price) {
      const cartItem = {
        id: image.id,
        name: image.title || image.name,
        price: image.price,
        image: image.publicUrl,
        category: image.category,
        description: image.description
      };
      addToCart(cartItem);
    }
  };



  // Get random image from category for categories section
  const getRandomImageFromCategory = (categoryName) => {
    const categoryImages = images.filter(image => image.category === categoryName);
    if (categoryImages.length > 0) {
      return categoryImages[Math.floor(Math.random() * categoryImages.length)];
    }
    return null;
  };



  // Dynamic categories with Supabase images
  const getDynamicCategories = () => {
    const baseCategories = [
      {
        id: 'Microcontrollers',
        name: 'Microcontrollers',
        icon: Smartphone,
        description: 'Arduino Uno, Mega, Nano & Atmega development boards',
        count: '5+ Boards'
      },
      {
        id: 'WiFi Modules',
        name: 'WiFi Modules',
        icon: Wifi,
        description: 'ESP32, ESP8266 & wireless development modules',
        count: '5+ Modules'
      },
      {
        id: 'Sensors',
        name: 'Sensors',
        icon: Heart,
        description: 'Temperature, motion, ultrasonic & specialty sensors',
        count: '15+ Sensors'
      },
      {
        id: 'Components',
        name: 'Components',
        icon: Palette,
        description: 'Breadboards, LEDs, resistors & essential components',
        count: '20+ Components'
      }
    ];

    // Add dynamic images from Supabase
    return baseCategories.map(category => {
      const randomImage = getRandomImageFromCategory(category.id);
      return {
        ...category,
        image: randomImage?.publicUrl || `https://fotcjsmnerawpqzhldhq.supabase.co/storage/v1/object/public/product-images/products/arduino-uno-r3-1748603951988-impc78.jpg` // Fallback
      };
    });
  };

  const categories = getDynamicCategories();

  const features = [
    {
      icon: Truck,
      title: 'Countrywide Delivery',
      description: 'Fast delivery across Zambia. Located in Lusaka, Chalala near ICU'
    },
    {
      icon: Headphones,
      title: 'Project Consultation',
      description: 'Expert help with EC3 projects and final year electronics assistance'
    },
    {
      icon: Award,
      title: 'Multiple Payment Options',
      description: 'Airtel Money, MTN Money, Bank Transfer, and Cash accepted'
    }
  ];



  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-gray-900 to-black text-white overflow-hidden">
        {/* Dynamic Background Slideshow */}
        {backgroundImages.length > 0 && (
          <div className="absolute inset-0">
            {backgroundImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentBgIndex ? 'opacity-60' : 'opacity-0'
                }`}
              >
                <img
                  src={image}
                  alt={`Product ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Overlay gradients */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center">
            {/* Main Content */}
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-white">{settings.headerText || 'Welcome to'}</span>
                <br />
                <span className="text-primary-400">{settings.siteName || 'Gifted Solutions'}</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
                Your premier destination for <span className="text-primary-400 font-semibold">electronic components</span>,
                Arduino projects, and <span className="text-primary-400 font-semibold">electronics consultation</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/shop"
                  className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <ShoppingBag className="mr-2" size={20} />
                  Shop Now
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link
                  to="/custom-request"
                  className="border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Custom Request
                </Link>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Echo Fireblocks and Handles Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Echo Fireblocks and Handles</h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Discover our range of eco-friendly Echo Fireblocks and durable handles designed for safety and efficiency.
          </p>
          {/* Add more content or components here as needed */}
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProductCategoriesGrid />
        </div>
      </section>

      {/* Video Section */}
      <VideoSection />

      {/* Categories Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Shop by Category
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our diverse collection of products across different categories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Link
                  key={category.id}
                  to={`/shop/${category.id}`}
                  className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary-200"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/200/200';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-300">
                        <IconComponent
                          size={32}
                          className="text-primary-500 group-hover:text-white transition-colors duration-300"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-semibold text-black mb-2 group-hover:text-primary-500 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-gray-600 mb-3">
                      {category.description}
                    </p>
                    <div className="text-sm font-medium text-primary-500">
                      {category.count}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>



      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Why Choose Gifted Solutions?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to providing exceptional service and quality products that exceed your expectations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-300">
                    <IconComponent size={36} className="text-primary-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-3 group-hover:text-primary-500 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-r from-black via-gray-900 to-black text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Build Your Next <span className="text-primary-400">Electronics Project</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Whether you need Arduino components, sensors, or project consultation for your EC3 or final year project,
              we have everything you need to bring your ideas to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/shop"
                className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <ShoppingBag className="mr-2" size={20} />
                Browse Components
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/custom-request"
                className="border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Project Consultation
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold py-4 px-10 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                WhatsApp: 0779421717
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Product Card Component - Updated design with full image and quantity controls
const ProductCard = ({ image, onAddToCart }) => {
  const [quantity, setQuantity] = useState(0);
  const [isInCart, setIsInCart] = useState(false);

  const handleAddToCart = () => {
    onAddToCart(image);
    setQuantity(1);
    setIsInCart(true);
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity <= 0) {
      setQuantity(0);
      setIsInCart(false);
    } else {
      setQuantity(newQuantity);
      // Update cart with new quantity
      onAddToCart({ ...image, quantity: newQuantity });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200">
      {/* Stock Status Badge */}
      <div className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1 text-center">
        {image.inStock ? 'In Stock' : 'Out of Stock'}
      </div>

      {/* Image Container - Full image fill */}
      <div className="aspect-square relative overflow-hidden">
        <img
          src={image.publicUrl}
          alt={image.title || image.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.src = '/api/placeholder/200/200';
          }}
        />
      </div>

      {/* Category Badge */}
      <div className="px-3 pt-2">
        <span className="inline-block bg-blue-500 text-white text-xs font-medium px-2 py-1 rounded">
          {image.category}
        </span>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* Title */}
        <h3 className="font-semibold text-gray-900 text-sm leading-tight">
          {image.title || image.name}
        </h3>

        {/* Description */}
        <p className="text-xs text-gray-600 line-clamp-2">
          {image.description}
        </p>

        {/* Price */}
        {image.price && (
          <div className="text-lg font-bold text-gray-900">
            K{image.price}
          </div>
        )}

        {/* Add to Cart Button or Quantity Controls */}
        {image.price && image.inStock && (
          <>
            {!isInCart ? (
              <button
                onClick={handleAddToCart}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-3 rounded text-sm transition-colors duration-200 flex items-center justify-center"
              >
                🛒 Add to Cart
              </button>
            ) : (
              <div className="flex items-center justify-between bg-gray-100 rounded p-2">
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-lg font-bold"
                >
                  −
                </button>
                <span className="text-lg font-semibold text-gray-900">
                  Qty: {quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center text-lg font-bold"
                >
                  +
                </button>
              </div>
            )}
          </>
        )}

        {/* Out of Stock Button */}
        {image.price && !image.inStock && (
          <button
            disabled
            className="w-full bg-gray-300 text-gray-500 font-medium py-2 px-3 rounded text-sm cursor-not-allowed"
          >
            Out of Stock
          </button>
        )}
      </div>
    </div>
  );
};

export default Home;
